import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CircleUser, KeyRound, LogOut } from "lucide-react";

import { cn } from "@/lib/utils";
import { useUserQuery } from "@/hooks/use-user";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

function getInitials(name: string | undefined, email: string | undefined) {
  const words = (name?.trim() || email?.split("@")[0] || "")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return words[0]?.slice(0, 2).toUpperCase() ?? "";
}

export function UserMenu() {
  const navigate = useNavigate();
  const { data: user, isLoading } = useUserQuery();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  if (isLoading) {
    return (
      <div className="flex h-11 items-center gap-2 px-2 md:h-8 md:px-2.5">
        <Skeleton className="size-6 rounded-full bg-sidebar-accent md:size-5" />
        <Skeleton className="h-3.5 w-28 rounded bg-sidebar-accent" />
      </div>
    );
  }

  // Some accounts carry their email as their name; showing it once is enough.
  const rawName = user?.name?.trim();
  const name = rawName && rawName !== user?.email ? rawName : undefined;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          ref={triggerRef}
          className={cn(
            "flex h-11 w-full cursor-pointer items-center gap-2 rounded-lg px-2 text-left transition-colors md:h-8 md:px-2.5",
            "hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          )}
        >
          <span
            aria-hidden="true"
            className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-background to-muted text-[10px] font-medium text-muted-foreground ring-1 ring-black/[0.08] md:size-5 md:text-[8px]"
          >
            {getInitials(name, user?.email)}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium md:text-[13px]">
            {name || user?.email || "Account"}
          </span>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="top"
          align="start"
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
        >
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            {name && (
              <span className="truncate text-[13px] font-medium text-foreground">
                {name}
              </span>
            )}
            <span className="truncate">{user?.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/settings/account">
              <CircleUser />
              Account
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/api-keys">
              <KeyRound />
              API keys
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setIsLogoutDialogOpen(true)}>
            <LogOut />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
      >
        <AlertDialogContent
          // The menu item that opened the dialog is gone by now, so hand focus
          // back to the menu's trigger instead of letting it fall to <body>.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            triggerRef.current?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Log out</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to log out? You will need to sign in again
              to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate("/logout")}>
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
